import {
    Component,
    OnDestroy,
    OnInit,
    ChangeDetectionStrategy,
} from '@angular/core';
import { StepNavigationService } from '../../../modules/application/shared/services/step-navigation/step-navigation.service';
import {
    BehaviorSubject,
    map,
    merge,
    Observable,
    share,
    Subject,
    take,
    takeUntil,
    tap,
    timer,
} from 'rxjs';
import { ApplicationPreparationQuestionnaireService } from '../../../modules/application/shared/services/application-preparation-questionnaire/application-preparation-questionnaire.service';
import { FormGroupType } from '../../../core/models/form-group-type';
import { ApplicationCreationRequest } from '../../../modules/application/shared/models/requests/application-creation-request';
import { FormGroup } from '@angular/forms';
import { ApplicationCreationStatus } from '../../../modules/application/shared/models/responses/application-creation-response';
import { AppStorageService } from '../../../core/services/app-storage/app-storage.service';

@Component({
    selector: 'app-application-preparation-base',
    changeDetection: ChangeDetectionStrategy.Eager,
    template:
        '<!-- This component serves as a base and does not render any content -->',
})
export abstract class ApplicationPreparationBaseComponent
    implements OnInit, OnDestroy
{
    protected static readonly largeApplicationTimeout = 45000;
    protected destroy$ = new Subject<void>();
    protected hasTimerElapsed$ = new BehaviorSubject<boolean>(false);

    abstract form: FormGroup<
        Partial<FormGroupType<ApplicationCreationRequest>>
    >;
    abstract shouldSubmit(): boolean;
    abstract shouldAddTimeout(): boolean;

    constructor(
        protected readonly stepService: StepNavigationService,
        protected readonly applicationPreparationQuestionnaireService: ApplicationPreparationQuestionnaireService,
        protected readonly appStorageService: AppStorageService,
    ) {}

    ngOnInit(): void {
        if (
            !this.requiredApplicationInfoExists() &&
            this.appStorageService.getContactCiviId() !== null
        ) {
            this.applicationPreparationQuestionnaireService.patchApplicationPreparationForm(
                {
                    civiId: this.appStorageService.getContactCiviId(),
                },
            );
        }

        this.stepService.nextClick$
            .pipe(takeUntil(this.destroy$))
            .subscribe(() =>
                this.handleApplicationPreparationStepContinuation(),
            );
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    protected handleApplicationPreparationStepContinuation(): void {
        if (this.form.valid) {
            this.applicationPreparationQuestionnaireService.patchApplicationPreparationForm(
                this.form.value,
            );

            if (this.shouldSubmit()) {
                const delayed$ =
                    this.applicationPreparationQuestionnaireService.submitApplicationPreparationForm(
                        this.hasTimerElapsed$,
                    );

                this.submitWithSoftTimeout(
                    delayed$,
                    ApplicationPreparationBaseComponent.largeApplicationTimeout,
                ).subscribe({
                    next: (status: ApplicationCreationStatus) => {
                        this.handleFormSubmission(status);
                    },
                });
            } else {
                this.stepService.goToNextStep();
            }
        } else {
            this.form.markAllAsTouched();
        }
    }

    /**
     * If applicable, adds a soft timeout to the source observable. Soft timeout keeps the subscription active,
     * but allows additional actions to be triggered when the timer elapses.
     */
    private submitWithSoftTimeout<T>(
        source$: Observable<T>,
        timeoutMs: number,
    ): Observable<T | ApplicationCreationStatus> {
        const shared$ = source$.pipe(share());

        const timeout$ = timer(timeoutMs).pipe(
            takeUntil(shared$),
            tap(() => {
                this.hasTimerElapsed$.next(true);
            }),
            map(() => ApplicationCreationStatus.Timeout),
            take(1),
        );

        this.appStorageService.clearContactCiviId();

        return merge(shared$, ...(this.shouldAddTimeout() ? [timeout$] : []));
    }

    /**
     * It assumes that all form submissions should be a success. If other status codes are acceptable, override this method
     * and handle the status and logic accordingly.
     * @param status
     */
    protected handleFormSubmission(status: ApplicationCreationStatus): void {
        if (status !== ApplicationCreationStatus.Success) {
            throw new Error(
                `Unexpected application creation status: ${status}.`,
            );
        }
    }

    private requiredApplicationInfoExists(): boolean {
        const appPreparationForm =
            this.applicationPreparationQuestionnaireService.getApplicationPreparationForm();
        const civiId = appPreparationForm.controls.civiId.value;
        return civiId !== null && civiId !== undefined;
    }
}
